/**
 * AddTreatsWizard - Multi-step wizard for adding treats to subscription
 * Step 1: Select treats
 * Step 2: Choose quantities
 * Step 3: Confirm and add
 */
import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Alert,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { QuizButton } from "./shared/QuizButton";
import { PawsLoader } from "./shared/PawsLoader";
import { addTreatsToCustomer } from "../api/accountProxy";
import { QUIZ } from "@/theme/quizTokens";

type AvailableTreat = {
  productId: string;
  productTitle: string;
  imageUrl: string | null;
  variantId: string;
  variantTitle: string;
  price: number;
};

type SelectedTreat = {
  productId: string;
  variantId: string;
  productTitle: string;
  imageUrl: string | null;
  price: number;
  quantity: number;
};

type AddTreatsWizardProps = {
  open: boolean;
  onClose: () => void;
  availableTreats: AvailableTreat[];
  proxyBase: string;
  shop: string;
  customerId?: string;
  onSuccess: () => void;
};

type Step = "select" | "quantity" | "confirm";

export function AddTreatsWizard({
  open,
  onClose,
  availableTreats,
  proxyBase,
  shop,
  customerId,
  onSuccess,
}: AddTreatsWizardProps) {
  const [step, setStep] = useState<Step>("select");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep("select");
      setSelectedIds(new Set());
      setQuantities({});
      setSuccess(false);
      setError(null);
    }
  }, [open]);

  // Toggle treat selection
  const toggleSelect = (productId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
        // Initialize quantity to 1 when selected
        setQuantities((q) => ({ ...q, [productId]: q[productId] || 1 }));
      }
      return next;
    });
  };

  // Update quantity
  const updateQuantity = (productId: string, qty: number) => {
    if (qty < 1) return;
    setQuantities((prev) => ({ ...prev, [productId]: qty }));
  };

  // Get selected treats with quantities
  const selectedTreats = useMemo<SelectedTreat[]>(() => {
    return availableTreats
      .filter((t) => selectedIds.has(t.productId))
      .map((t) => ({
        productId: t.productId,
        variantId: t.variantId,
        productTitle: t.productTitle,
        imageUrl: t.imageUrl,
        price: t.price,
        quantity: quantities[t.productId] || 1,
      }));
  }, [availableTreats, selectedIds, quantities]);

  // Calculate total
  const total = useMemo(() => {
    return selectedTreats.reduce((sum, t) => sum + t.price * t.quantity, 0);
  }, [selectedTreats]);

  // Handle confirm/save
  const handleConfirm = async () => {
    if (selectedTreats.length === 0) return;

    setSaving(true);
    setError(null);

    try {
      const treatsToAdd = selectedTreats.map((t) => ({
        variantId: t.variantId,
        quantity: t.quantity,
      }));

      const result = await addTreatsToCustomer(proxyBase, shop, treatsToAdd, customerId);

      if (!result.ok) {
        throw new Error(result.error || "Failed to add treats");
      }

      setSuccess(true);
      onSuccess();

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to add treats");
    } finally {
      setSaving(false);
    }
  };

  // Navigation
  const canProceedToQuantity = selectedIds.size > 0;
  const canProceedToConfirm = selectedTreats.length > 0;

  const goBack = () => {
    if (step === "quantity") setStep("select");
    else if (step === "confirm") setStep("quantity");
  };

  const goNext = () => {
    if (step === "select" && canProceedToQuantity) setStep("quantity");
    else if (step === "quantity" && canProceedToConfirm) setStep("confirm");
  };

  // Step titles
  const stepTitles: Record<Step, string> = {
    select: "Choose Treats",
    quantity: "Select Quantities",
    confirm: "Confirm Order",
  };

  const stepNumbers: Record<Step, number> = {
    select: 1,
    quantity: 2,
    confirm: 3,
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          maxHeight: { xs: "100%", sm: "85vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          borderBottom: "1px solid rgba(0,0,0,.08)",
          background: QUIZ.color.cream,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {step !== "select" && !success && (
            <IconButton
              onClick={goBack}
              disabled={saving}
              sx={{ mr: 0.5, color: QUIZ.color.brown }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #E9DFD7 0%, #FFC7C3 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CardGiftcardIcon sx={{ fontSize: 22, color: QUIZ.color.brown }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 800,
                color: QUIZ.color.brown,
              }}
            >
              {stepTitles[step]}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 12, md: 13 },
                color: QUIZ.color.brown,
                opacity: 0.65,
              }}
            >
              Step {stepNumbers[step]} of 3
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{
            color: QUIZ.color.brown,
            "&:hover": { background: "rgba(0,0,0,.05)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: { xs: 2, md: 3 },
          background: QUIZ.color.warmWhite,
        }}
      >
        {/* Saving State */}
        {saving && (
          <PawsLoader
            action="treats"
            fullScreen={false}
            size="medium"
          />
        )}

        {/* Success State */}
        {success && !saving && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: "12px",
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            Treats added successfully! They'll be included in your next order.
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{ borderRadius: "12px", mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Step 1: Select Treats */}
        {step === "select" && !success && !saving && (
          <>
            <Typography
              sx={{
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.brown,
                opacity: 0.75,
                mb: 2,
              }}
            >
              Select the treats you'd like to add to your subscription
            </Typography>

            {availableTreats.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CardGiftcardIcon
                  sx={{ fontSize: 48, color: QUIZ.color.brown, opacity: 0.3, mb: 2 }}
                />
                <Typography sx={{ color: QUIZ.color.brown, opacity: 0.6 }}>
                  No treats available to add
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {availableTreats.map((treat) => {
                  const isSelected = selectedIds.has(treat.productId);

                  return (
                    <Box
                      key={treat.productId}
                      onClick={() => toggleSelect(treat.productId)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1.5, md: 2 },
                        p: { xs: 1.25, md: 1.5 },
                        borderRadius: "12px",
                        border: isSelected
                          ? "2px solid #4A3026"
                          : "1px solid rgba(0,0,0,0.08)",
                        background: isSelected ? "#FFF5F5" : QUIZ.color.cream,
                        cursor: "pointer",
                        transition: "all 150ms ease",
                        "&:hover": {
                          borderColor: isSelected ? QUIZ.color.brown : "rgba(0,0,0,.15)",
                        },
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        sx={{
                          p: 0,
                          color: "rgba(0,0,0,.2)",
                          "&.Mui-checked": { color: QUIZ.color.brown },
                        }}
                      />

                      {treat.imageUrl ? (
                        <Box
                          component="img"
                          src={treat.imageUrl}
                          alt={treat.productTitle}
                          sx={{
                            width: { xs: 50, md: 56 },
                            height: { xs: 50, md: 56 },
                            objectFit: "cover",
                            borderRadius: "8px",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: { xs: 50, md: 56 },
                            height: { xs: 50, md: 56 },
                            borderRadius: "8px",
                            background: QUIZ.color.cream,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CardGiftcardIcon
                            sx={{ fontSize: 24, color: QUIZ.color.brown, opacity: 0.4 }}
                          />
                        </Box>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 14, md: 15 },
                            color: QUIZ.color.brown,
                          }}
                          noWrap
                        >
                          {treat.productTitle}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: 13, md: 14 },
                            fontWeight: 600,
                            color: QUIZ.color.brown,
                          }}
                        >
                          £{treat.price.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </>
        )}

        {/* Step 2: Quantities */}
        {step === "quantity" && !success && !saving && (
          <>
            <Typography
              sx={{
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.brown,
                opacity: 0.75,
                mb: 2,
              }}
            >
              How many of each treat would you like per delivery?
            </Typography>

            <Stack spacing={1.5}>
              {selectedTreats.map((treat) => (
                <Box
                  key={treat.productId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2 },
                    p: { xs: 1.25, md: 1.5 },
                    borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: QUIZ.color.cream,
                  }}
                >
                  {treat.imageUrl ? (
                    <Box
                      component="img"
                      src={treat.imageUrl}
                      alt={treat.productTitle}
                      sx={{
                        width: { xs: 50, md: 56 },
                        height: { xs: 50, md: 56 },
                        objectFit: "cover",
                        borderRadius: "8px",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: { xs: 50, md: 56 },
                        height: { xs: 50, md: 56 },
                        borderRadius: "8px",
                        background: QUIZ.color.cream,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <CardGiftcardIcon
                        sx={{ fontSize: 24, color: QUIZ.color.brown, opacity: 0.4 }}
                      />
                    </Box>
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: 14, md: 15 },
                        color: QUIZ.color.brown,
                      }}
                      noWrap
                    >
                      {treat.productTitle}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 12, md: 13 },
                        color: QUIZ.color.brown,
                      }}
                    >
                      £{treat.price.toFixed(2)} each
                    </Typography>
                  </Box>

                  {/* Quantity Controls */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      background: QUIZ.color.warmWhite,
                      borderRadius: "999px",
                      padding: "4px 8px",
                      border: "1px solid #E2E2E2",
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        updateQuantity(treat.productId, treat.quantity - 1)
                      }
                      disabled={treat.quantity <= 1}
                      size="small"
                      sx={{
                        width: 28,
                        height: 28,
                        color: QUIZ.color.brown,
                        "&.Mui-disabled": { opacity: 0.3 },
                      }}
                    >
                      <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        minWidth: 24,
                        textAlign: "center",
                        fontSize: 14,
                        color: QUIZ.color.brown,
                      }}
                    >
                      {treat.quantity}
                    </Typography>

                    <IconButton
                      onClick={() =>
                        updateQuantity(treat.productId, treat.quantity + 1)
                      }
                      size="small"
                      sx={{ width: 28, height: 28, color: QUIZ.color.brown }}
                    >
                      <AddRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>

                  {/* Line total */}
                  <Typography
                    sx={{
                      fontSize: { xs: 14, md: 15 },
                      fontWeight: 700,
                      color: QUIZ.color.brown,
                      minWidth: 60,
                      textAlign: "right",
                    }}
                  >
                    £{(treat.price * treat.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Step 3: Confirm */}
        {step === "confirm" && !success && !saving && (
          <>
            <Alert
              severity="info"
              icon={<ShoppingCartIcon />}
              sx={{
                borderRadius: "12px",
                mb: 3,
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              These treats will be added to your subscription and included in
              every delivery.
            </Alert>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 15 },
                fontWeight: 700,
                color: QUIZ.color.brown,
                mb: 1.5,
              }}
            >
              Order Summary
            </Typography>

            <Stack spacing={1} sx={{ mb: 3 }}>
              {selectedTreats.map((treat) => (
                <Box
                  key={treat.productId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: "1px solid rgba(0,0,0,.06)",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 13, md: 14 },
                        fontWeight: 600,
                        color: QUIZ.color.brown,
                      }}
                    >
                      {treat.productTitle}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 12, md: 13 },
                        color: QUIZ.color.brown,
                        opacity: 0.65,
                      }}
                    >
                      £{treat.price.toFixed(2)} × {treat.quantity}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: 14, md: 15 },
                      fontWeight: 700,
                      color: QUIZ.color.brown,
                    }}
                  >
                    £{(treat.price * treat.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderRadius: "12px",
                background: QUIZ.color.cream,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 15 },
                  fontWeight: 700,
                  color: QUIZ.color.brown,
                }}
              >
                Added to next order
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 20, md: 22 },
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                }}
              >
                +£{total.toFixed(2)}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      {!success && !saving && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(0,0,0,.08)",
            background: QUIZ.color.cream,
            flexShrink: 0,
          }}
        >
          {/* Selection count for step 1 */}
          {step === "select" && selectedIds.size > 0 && (
            <Typography
              sx={{
                fontSize: 13,
                color: QUIZ.color.brown,
                fontWeight: 600,
                mb: 1.5,
                textAlign: "center",
              }}
            >
              {selectedIds.size} treat{selectedIds.size !== 1 ? "s" : ""} selected
            </Typography>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <QuizButton
              variant="secondary"
              onClick={onClose}
              disabled={saving}
              fullWidth
            >
              Cancel
            </QuizButton>

            {step === "select" && (
              <QuizButton
                variant="primary"
                onClick={goNext}
                disabled={!canProceedToQuantity}
                fullWidth
              >
                Next: Quantities
              </QuizButton>
            )}

            {step === "quantity" && (
              <QuizButton
                variant="primary"
                onClick={goNext}
                disabled={!canProceedToConfirm}
                fullWidth
              >
                Next: Confirm
              </QuizButton>
            )}

            {step === "confirm" && (
              <QuizButton
                variant="primary"
                onClick={handleConfirm}
                fullWidth
              >
                Add Treats (+£{total.toFixed(2)})
              </QuizButton>
            )}
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
